import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Handler from "../../../Helpers/Handler";
import { formatIST } from "../../../Helpers/Utils";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";

const PAGE_LIMIT = 10;

/* ── Debounce hook ── */
function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// const BASE_URL = 'http://localhost/business_community/';
const BASE_URL = 'https://qnexus.in/community/';
const avatarLetter = (name = '') => name.trim().charAt(0).toUpperCase() || '?';

const CommunityMembers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { community_id, community_name, is_creator } = location.state || {};

  const [members,  setMembers]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");
  const [search,   setSearch]   = useState("");
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);

  // Remove confirm
  const [removeId,  setRemoveId]  = useState(null);
  const [removing,  setRemoving]  = useState(false);

  // Invite modal
  const [showInvite,    setShowInvite]    = useState(false);
  const [inviteSearch,  setInviteSearch]  = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteSearching, setInviteSearching] = useState(false);
  const [inviting,      setInviting]      = useState(null); // user_id being invited
  const [inviteError,   setInviteError]   = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");

  const debouncedInviteSearch = useDebounce(inviteSearch, 350);

  /* ── Fetch members ── */
  const fetchMembers = useCallback(async (p = 1, q = "") => {
    if (!community_id) return;
    setLoading(true);
    setError("");
    const res = await Handler({
      method: "GET",
      url: `/communities/community_members_list.php?community_id=${community_id}&page=${p}&limit=${PAGE_LIMIT}&q=${encodeURIComponent(q)}`,
    });
    setLoading(false);
    if (res.success) {
      setMembers(res.data?.list || []);
      setTotal(res.data?.pagination?.total || 0);
    } else {
      setError(res.message || "Failed to fetch members.");
    }
  }, [community_id]);

  useEffect(() => { fetchMembers(page, search); }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchMembers(1, search); }, 380);
    return () => clearTimeout(t);
  }, [search]);

  /* ── Search users for invite modal ── */
  useEffect(() => {
    if (!showInvite) return;
    if (!debouncedInviteSearch.trim()) { setInviteResults([]); return; }

    let cancelled = false;
    (async () => {
      setInviteSearching(true);
      try {
        const res = await Handler({
          method: "GET",
          url: `/users/search_list.php?search=${encodeURIComponent(debouncedInviteSearch)}&limit=10`,
        });
        if (!cancelled && res?.success) {
          setInviteResults(res.data?.list || []);
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setInviteSearching(false);
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedInviteSearch, showInvite]);

  /* ── Remove member ── */
  const handleRemove = async (user_id) => {
    setRemoving(true);
    const res = await Handler({
      method: "POST",
      url: "/communities/members_remove.php",
      data: { community_id, user_id },
    });
    setRemoving(false);
    setRemoveId(null);
    if (res.success) {
      setSuccess("Member removed successfully.");
      setTimeout(() => setSuccess(""), 2500);
      fetchMembers(page, search);
    } else {
      setError(res.message || "Failed to remove member.");
    }
  };

  /* ── Invite user ── */
  const handleInvite = async (user) => {
    setInviteError("");
    setInviteSuccess("");
    setInviting(user.user_id);

    const res = await Handler({
      method: "POST",
      url: "/communities/invite.php",
      data: { community_id, invited_user_id: user.user_id },
    });
    setInviting(null);

    if (res.success) {
      setInviteSuccess(`Invite sent to ${user.business_name}!`);
      setInviteSearch("");
      setInviteResults([]);
    } else {
      setInviteError(res.message || "Failed to send invite.");
    }
  };

  const closeInviteModal = () => {
    setShowInvite(false);
    setInviteSearch("");
    setInviteResults([]);
    setInviteError("");
    setInviteSuccess("");
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  return (
    <MainLayout>
      <FeedLayout>
        <div className="comm-page">

          {/* Header */}
          <div className="comm-header">
            <h2>{community_name || "Community"} — Members</h2>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="comm-btn-outline" onClick={() => setShowInvite(true)}>
                <i className="bx bx-user-plus" /> Invite
              </button>
              <button className="back-btn" onClick={() => navigate(-1)}>
                <i className="bx bx-arrow-back" /> Back
              </button>
            </div>
          </div>

          {error   && <div className="comm-alert error"  ><i className="bx bx-error-circle"  />{error}  </div>}
          {success && <div className="comm-alert success"><i className="bx bx-check-circle"  />{success}</div>}

          {/* Search */}
          <div className="comm-list-toolbar">
            <div className="comm-search-wrap">
              <i className="bx bx-search" />
              <input
                type="text"
                placeholder="Search by name, mobile, interest…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span style={{ color: "var(--c-muted)", fontSize: "0.82rem" }}>
              {total} member{total !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Member Cards */}
          {loading ? (
            <div className="comm-spinner" />
          ) : members.length === 0 ? (
            <div className="comm-empty">
              <i className="bx bx-group" />
              <p>No members found.</p>
            </div>
          ) : (
            <div className="member-grid">
              {members.map((m) => (
                <div key={m.user_id} className="member-card">
                  <div className="member-avatar">{(m.name || "?").charAt(0).toUpperCase()}</div>
                  <div className="member-info">
                    <div className="name">{m.name}</div>
                    <div className="meta">
                      {m.mobile    && <span><i className="bx bx-phone"    /> {m.mobile}</span>}
                      {m.email     && <span><i className="bx bx-envelope" /> {m.email}</span>}
                      {m.joined_at && <span><i className="bx bx-calendar" /> {formatIST(m.joined_at)}</span>}
                      {m.categories && <span><i className="bx bx-tag"     /> {m.categories}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className={`member-role ${m.role?.toLowerCase()}`}>{m.role}</span>
                    {is_creator && m.role !== "ADMIN" && (
                      removeId === m.user_id ? (
                        <>
                          <button
                            className="comm-btn-danger"
                            style={{ padding: "6px 12px", fontSize: "0.78rem" }}
                            onClick={() => handleRemove(m.user_id)}
                            disabled={removing}
                          >
                            {removing
                              ? <i className="bx bx-loader-alt bx-spin" />
                              : <i className="bx bx-check" />} Confirm
                          </button>
                          <button
                            className="comm-btn-outline"
                            style={{ padding: "6px 12px", fontSize: "0.78rem" }}
                            onClick={() => setRemoveId(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button className="tbl-btn danger" onClick={() => setRemoveId(m.user_id)}>
                          <i className="bx bx-user-minus" /> Remove
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && total > PAGE_LIMIT && (
            <div className="comm-pagination">
              <button
                className="comm-page-btn"
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                <i className="bx bx-chevron-left" /> Prev
              </button>
              <span className="comm-page-info">Page {page} of {totalPages}</span>
              <button
                className="comm-page-btn"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                Next <i className="bx bx-chevron-right" />
              </button>
            </div>
          )}

          {/* Invite Modal */}
          {showInvite && (
            <div className="comm-modal-overlay" onClick={closeInviteModal}>
              <div className="comm-modal" onClick={(e) => e.stopPropagation()}>

                <button className="comm-modal-close" onClick={closeInviteModal}>
                  <i className="bx bx-x" />
                </button>
                <h5><i className="bx bx-user-plus" /> Invite User</h5>

                {inviteError   && <div className="comm-alert error"  ><i className="bx bx-error-circle" />{inviteError}  </div>}
                {inviteSuccess && <div className="comm-alert success"><i className="bx bx-check-circle" />{inviteSuccess}</div>}

                {/* Search input */}
                <div className="comm-form">
                  <div className="form-group" style={{ position: "relative" }}>
                    <label>Search User</label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search by business name, city or phone…"
                      value={inviteSearch}
                      onChange={(e) => { setInviteSearch(e.target.value); setInviteError(""); }}
                      autoComplete="off"
                      autoFocus
                    />
                    {inviteSearching && (
                      <span style={{ position: "absolute", right: 10, top: 36 }}>
                        <i className="bx bx-loader-alt bx-spin" style={{ color: "var(--c-muted)" }} />
                      </span>
                    )}
                  </div>
                </div>

                {/* Results */}
                <div className="chat-user-list" style={{ maxHeight: 280, overflowY: "auto" }}>
                  {!inviteSearch.trim() ? (
                    <p className="chat-modal-hint">Start typing to search users…</p>
                  ) : inviteSearching ? null : inviteResults.length === 0 ? (
                    <p className="chat-modal-hint">No users found for "{inviteSearch}"</p>
                  ) : (
                    inviteResults.map((user) => (
                      <div
                        key={user.user_id}
                        className="chat-user-item"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleInvite(user)}
                      >
                        {/* Avatar */}
                        <div className="chat-avatar" style={{ flexShrink: 0 }}>
                          <div className="chat-avatar__img">
                            {user.logo
                              ? <img
                                  src={`${BASE_URL}${user.logo}`}
                                  alt={user.business_name}
                                  onError={e => { e.target.style.display = 'none'; }}
                                />
                              : avatarLetter(user.business_name)
                            }
                          </div>
                        </div>

                        {/* Info */}
                        <div className="chat-user-item__info">
                          <span className="chat-user-item__name">{user.business_name}</span>
                          {(user.city || user.area) && (
                            <span className="chat-user-item__meta">
                              📍 {[user.area, user.city].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>

                        {/* Spinner while inviting this specific user */}
                        {inviting === user.user_id && (
                          <i className="bx bx-loader-alt bx-spin" style={{ marginLeft: "auto", color: "var(--c-primary)" }} />
                        )}
                      </div>
                    ))
                  )}
                </div>

                <div className="comm-modal-actions">
                  <button className="comm-btn-outline" onClick={closeInviteModal}>Close</button>
                </div>

              </div>
            </div>
          )}

        </div>
      </FeedLayout>
    </MainLayout>
  );
};

export default CommunityMembers;